import { useCallback, useEffect, useState } from "react";
import { getAdminUsageMetrics } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Activity, FileText, Stethoscope, Users } from "lucide-react";

const REPORT_LABELS = {
  INFORME_CLINICO: "Documento clínico",
  INFORME_FAMILIAR: "Documento para familia",
  RESUMEN_CLINICO: "Resumen clínico",
};

function MiniStatRow({ label, value }) {
  return (
    <div className="flex justify-between items-center py-1 text-sm border-b border-slate-100 last:border-0">
      <span className="text-slate-600">{label}</span>
      <span className="font-semibold text-slate-800 tabular-nums">{value}</span>
    </div>
  );
}

function DualTrendChart({ consultationsByDay, reportsByDay }) {
  const dates =
    consultationsByDay?.length >= (reportsByDay?.length || 0)
      ? consultationsByDay.map((d) => d.date)
      : (reportsByDay || []).map((d) => d.date);
  if (!dates.length) {
    return (
      <div className="h-56 flex items-center justify-center text-slate-400 text-sm border border-dashed rounded-lg">
        Sin datos en este período
      </div>
    );
  }
  const consMap = Object.fromEntries((consultationsByDay || []).map((d) => [d.date, d.count]));
  const repMap = Object.fromEntries((reportsByDay || []).map((d) => [d.date, d.count]));
  const maxVal = Math.max(
    1,
    ...dates.map((d) => (consMap[d] || 0) + (repMap[d] || 0)),
    ...dates.map((d) => Math.max(consMap[d] || 0, repMap[d] || 0)),
  );
  return (
    <div className="space-y-3">
      <div className="flex gap-4 text-xs text-slate-600">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-sky-500" />
          Consultas creadas
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-violet-500" />
          Informes generados
        </span>
      </div>
      <div className="flex items-end gap-px sm:gap-0.5 h-52 pt-2 border-l border-b border-slate-200 pl-1">
        {dates.map((date) => {
          const c = consMap[date] || 0;
          const r = repMap[date] || 0;
          const ch = Math.max(3, (c / maxVal) * 100);
          const rh = Math.max(3, (r / maxVal) * 100);
          return (
            <div key={date} className="flex-1 flex gap-0.5 items-end justify-center h-full min-w-0">
              <div
                className="w-[42%] max-w-[14px] bg-sky-400 rounded-t"
                style={{ height: `${ch}%` }}
                title={`${date}: ${c} consultas`}
              />
              <div
                className="w-[42%] max-w-[14px] bg-violet-500 rounded-t"
                style={{ height: `${rh}%` }}
                title={`${date}: ${r} informes`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-slate-500 px-0 gap-1 overflow-x-auto">
        {dates.map((d) => (
          <span key={d} className="flex-shrink-0">
            {d.slice(5)}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function AdminUsageDashboard() {
  const [trendDays, setTrendDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminUsageMetrics(trendDays);
      setData(res.data);
    } catch (e) {
      setData(null);
      toast.error("No se pudieron cargar las métricas de uso.");
    } finally {
      setLoading(false);
    }
  }, [trendDays]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <div className="space-y-6 animate-pulse max-w-6xl">
        <div className="h-8 w-64 bg-slate-200 rounded" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-28 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const g7 = data?.global_7d || {};
  const g30 = data?.global_30d || {};
  const rt7 = g7.reports_by_type || {};
  const rt30 = g30.reports_by_type || {};

  return (
    <div className="space-y-8 max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Uso del sistema</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Actividad agregada para validar adopción. No se muestran datos clínicos ni de pacientes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 uppercase tracking-wide">Tendencia y tabla</span>
          <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
            <Button
              type="button"
              variant={trendDays === 7 ? "secondary" : "ghost"}
              size="sm"
              className="text-xs"
              onClick={() => setTrendDays(7)}
            >
              7 días
            </Button>
            <Button
              type="button"
              variant={trendDays === 30 ? "secondary" : "ghost"}
              size="sm"
              className="text-xs"
              onClick={() => setTrendDays(30)}
            >
              30 días
            </Button>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Activity className="h-4 w-4" />
          Métricas globales
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Médicos activos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <MiniStatRow label="Últimos 7 días" value={g7.active_doctors ?? 0} />
              <MiniStatRow label="Últimos 30 días" value={g30.active_doctors ?? 0} />
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                <Stethoscope className="h-4 w-4" />
                Consultas creadas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <MiniStatRow label="Últimos 7 días" value={g7.consultations_created ?? 0} />
              <MiniStatRow label="Últimos 30 días" value={g30.consultations_created ?? 0} />
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Informes generados
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <MiniStatRow label="Últimos 7 días" value={g7.reports_generated ?? 0} />
              <MiniStatRow label="Últimos 30 días" value={g30.reports_generated ?? 0} />
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Desglose de informes (7 días)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {Object.entries(REPORT_LABELS).map(([key, lab]) => (
              <div key={key} className="flex justify-between">
                <span className="text-slate-600">{lab}</span>
                <span className="font-medium tabular-nums">{rt7[key] ?? 0}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Desglose de informes (30 días)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {Object.entries(REPORT_LABELS).map(([key, lab]) => (
              <div key={key} className="flex justify-between">
                <span className="text-slate-600">{lab}</span>
                <span className="font-medium tabular-nums">{rt30[key] ?? 0}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">
            Tendencia diaria (últimos {data?.trend_days ?? trendDays} días)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DualTrendChart
            consultationsByDay={data?.trend?.consultations_by_day}
            reportsByDay={data?.trend?.reports_by_day}
          />
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Actividad por médico (período seleccionado)</CardTitle>
          <p className="text-xs text-slate-500 font-normal mt-1">
            Solo nombre y conteos. Sin identificadores de pacientes.
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th className="pb-2 pr-4 font-medium">Médico</th>
                <th className="pb-2 pr-4 font-medium tabular-nums">Consultas</th>
                <th className="pb-2 pr-4 font-medium tabular-nums">Informes</th>
                <th className="pb-2 font-medium">Última actividad</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_doctor || []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-slate-400">
                    Sin actividad en este período
                  </td>
                </tr>
              ) : (
                (data?.by_doctor || []).map((row) => (
                  <tr key={row.doctor_id} className="border-b border-slate-100">
                    <td className="py-2.5 pr-4 text-slate-800">{row.nombre || "—"}</td>
                    <td className="py-2.5 pr-4 tabular-nums">{row.consultations_created}</td>
                    <td className="py-2.5 pr-4 tabular-nums">{row.reports_generated}</td>
                    <td className="py-2.5 text-slate-600 whitespace-nowrap">
                      {row.last_activity_at
                        ? new Date(row.last_activity_at).toLocaleString("es-GT", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })
                        : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
